import type { BankLine, CapitalCall, Holder, KycCase } from '../types';

export function buildKycCases(holders: Holder[]): KycCase[] {
  const fides = holders.filter((holder) => holder.tenantId === 't-fides');
  const refer = fides.filter((holder) => holder.kycStatus === 'REFER');
  const expired = fides.filter((holder) => holder.kycStatus === 'EXPIRED');
  const pep = fides.filter((holder) => holder.pep);
  const cases: KycCase[] = [
    {
      id: 'kyc-1',
      applicationId: 'app-3',
      subjectName: 'Imogen Wren (director, Wren Holdings)',
      type: 'ID_MISMATCH',
      severity: 'MEDIUM',
      openedAt: '2026-09-30',
      status: 'OPEN',
      provider: 'GreenID',
      detail:
        'Date of birth on driver licence does not match the electoral roll record. Second ID document requested.',
    },
    {
      id: 'kyc-2',
      holderId: 'h-bennett-ind',
      subjectName: 'Olivia Bennett',
      type: 'WHOLESALE_EXPIRING',
      severity: 'LOW',
      openedAt: '2026-10-01',
      status: 'OPEN',
      provider: 'Registry rules',
      detail: 'Accountant certificate expires 30 Nov 2026. Renewal task sent to investor.',
    },
    {
      id: 'kyc-3',
      holderId: 'h-bennett-smsf',
      subjectName: 'Bennett Superannuation Fund',
      type: 'WHOLESALE_EXPIRING',
      severity: 'LOW',
      openedAt: '2026-10-01',
      status: 'OPEN',
      provider: 'Registry rules',
      detail: 'Accountant certificate expires 30 Nov 2026.',
    },
  ];
  refer.forEach((holder, index) =>
    cases.push({
      id: `kyc-r${index}`,
      holderId: holder.id,
      subjectName: holder.contactName,
      type: index % 2 ? 'SANCTIONS_POTENTIAL' : 'ADVERSE_MEDIA',
      severity: index % 2 ? 'HIGH' : 'MEDIUM',
      openedAt: '2026-10-09',
      status: index === 0 ? 'IN_REVIEW' : 'OPEN',
      provider: 'Dow Jones Risk & Compliance (demo)',
      detail:
        index % 2
          ? 'Potential name match on DFAT consolidated sanctions list (score 82%). Compare date of birth and nationality.'
          : 'Adverse media hit: news article naming a person with the same name in a civil dispute. Likely false positive.',
      assignedTo: index === 0 ? 'Daniel Osei' : undefined,
    }),
  );
  expired.forEach((holder, index) =>
    cases.push({
      id: `kyc-e${index}`,
      holderId: holder.id,
      subjectName: holder.name,
      type: 'PERIODIC_REFRESH',
      severity: 'LOW',
      openedAt: holder.kycRefreshDue ?? '2026-10-01',
      status: 'OPEN',
      provider: 'Registry rules',
      detail:
        'Three-year KYC refresh is overdue. Investor task issued; distributions continue while under review.',
    }),
  );
  pep.forEach((holder, index) =>
    cases.push({
      id: `kyc-p${index}`,
      holderId: holder.id,
      subjectName: holder.contactName,
      type: 'PEP_MATCH',
      severity: 'HIGH',
      openedAt: '2026-09-18',
      status: index === 0 ? 'ESCALATED' : 'OPEN',
      provider: 'Dow Jones Risk & Compliance (demo)',
      detail:
        'Domestic PEP match (local government councillor). Enhanced due diligence and source of wealth required.',
      assignedTo: 'Daniel Osei',
    }),
  );
  cases.push({
    id: 'kyc-u1',
    applicationId: 'app-2',
    subjectName: 'Calder Super Fund',
    type: 'UBO_INCOMPLETE',
    severity: 'MEDIUM',
    openedAt: '2026-10-03',
    status: 'OPEN',
    provider: 'Registry rules',
    detail: 'Second trustee director not yet verified. Invitation to verify sent 3 Oct.',
  });
  return cases;
}

export function buildBankLines(call: CapitalCall, holders: Holder[]): BankLine[] {
  const lines: BankLine[] = [];
  const nameOf = (holderId: string) =>
    holders.find((holder) => holder.id === holderId)?.name ?? holderId;
  call.lines
    .filter((line) => line.status !== 'PAID')
    .slice(0, 5)
    .forEach((line, index) => {
      const outstanding = line.amount - line.paid;
      lines.push({
        id: `bl-c${index}`,
        fundId: 'f-opp',
        date: `2026-10-${String(11 + index).padStart(2, '0')}`,
        amount: index === 3 ? outstanding - 50 : outstanding,
        description: `BPAY ${nameOf(line.holderId).split(' ')[0].toUpperCase()}`,
        reference: index === 4 ? 'CALL3' : line.crn,
        status: index < 3 ? 'SUGGESTED' : 'UNMATCHED',
        suggestedHolderId: index < 3 ? line.holderId : undefined,
        suggestedCallId: index < 3 ? call.id : undefined,
      });
    });
  lines.push({
    id: 'bl-a1',
    fundId: 'f-sdf2',
    date: '2026-10-15',
    amount: 250_000,
    description: 'EFT VASQUEZ T & C',
    reference: 'APP-26-1043',
    status: 'SUGGESTED',
    suggestedApplicationId: 'app-4',
  });
  lines.push({
    id: 'bl-a2',
    fundId: 'f-psd',
    date: '2026-10-15',
    amount: 400_000,
    description: 'EFT MOREAU SUPER',
    reference: 'MOREAU SMSF',
    status: 'UNMATCHED',
  });
  lines.push({
    id: 'bl-x1',
    fundId: 'f-psd',
    date: '2026-10-14',
    amount: 18.5,
    description: 'BANK INTEREST',
    reference: 'INT OCT',
    status: 'UNMATCHED',
  });
  lines.push({
    id: 'bl-m1',
    fundId: 'f-psd',
    date: '2026-10-09',
    amount: 200_000,
    description: 'EFT LARKSPUR PTY LTD',
    reference: 'APP-26-1051',
    status: 'MATCHED',
    matchedBy: 'Alex Morgan',
  });
  return lines;
}
